import SEO from "@/components/SEO";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BlogPostFrontmatter } from "../../types/mdx";

interface MdxModule {
  frontmatter: BlogPostFrontmatter;
}

const POSTS_PER_PAGE = 5;

const BlogIndexPage: React.FC = () => {
  const [posts, setPosts] = useState<BlogPostFrontmatter[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const mdxModules = import.meta.glob<{ frontmatter: BlogPostFrontmatter }>("/src/content/blog/*.mdx", {
      eager: true,
    });

    const allPosts: BlogPostFrontmatter[] = Object.values(mdxModules)
      .map((module) => module.frontmatter)
      .filter((frontmatter) => frontmatter && frontmatter.slug && frontmatter.title && frontmatter.date)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    setPosts(allPosts);
    setLoading(false);
  }, []);

  // Calculate pagination
  const totalPages = Math.ceil(posts.length / POSTS_PER_PAGE);
  const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
  const endIndex = startIndex + POSTS_PER_PAGE;
  const currentPosts = posts.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top of content
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-8 h-8 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-zinc-300 font-medium">Loading amazing content...</p>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen">
      {/* SEO Meta Tags */}
      <SEO
        title="Pulse Player Blog - Haptic Music Technology & Immersive Audio"
        description="Discover the latest in haptic music technology, behind-the-scenes insights, and the future of immersive audio experiences with Pulse Player."
        keywords={[
          "haptic music",
          "audio technology",
          "immersive audio",
          "music innovation",
          "sound technology",
          "pulse player",
        ]}
        url="/blog"
        type="website"
      />
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 "></div>
        <div className="relative px-6 py-16 md:py-24 max-w-6xl mx-auto">
          <div className="text-center">
            <h1 className="text-5xl md:text-7xl font-bold bg-gradient-to-r from-zinc-400 via-blue-400 to-purple-400 bg-clip-text text-transparent mb-6 min-h-16 md:min-h-24">
              Pulse Player Blog
            </h1>
            <p className="text-xl md:text-2xl text-zinc-300 max-w-3xl mx-auto leading-relaxed">
              Discover the latest in haptic music technology, behind-the-scenes insights, and the future of immersive
              audio experiences.
            </p>
          </div>
        </div>
      </div>{" "}
      {/* Content Section */}
      <div className="px-3 pb-16 max-w-6xl mx-auto">
        {posts.length === 0 ? (
          <div className="text-center py-16">
            <div className="bg-zinc-800/40 backdrop-blur-sm border border-zinc-700/50 rounded-2xl p-12 max-w-2xl mx-auto">
              <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-yellow-500 to-blue-500 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                  />
                </svg>
              </div>
              <h3 className="text-3xl font-bold text-white mb-4">Coming Soon</h3>
              <p className="text-zinc-400 text-lg leading-relaxed">
                Amazing content is being crafted. Stay tuned for insights that will reshape how you think about music
                technology!
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Featured Articles Section - First Page Only */}
            {currentPage === 1 && (
              <div className="mb-16">
                <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 mb-16">
                  {currentPosts.slice(0, 3).map((post, index) => {
                    const categories = {
                      0: { icon: "🧠", category: "Neuroscience", color: "from-purple-500 to-blue-600" },
                      1: { icon: "💝", category: "Psychology", color: "from-pink-500 to-red-600" },
                      2: { icon: "🤝", category: "Accessibility", color: "from-green-500 to-teal-600" },
                    };
                    const categoryInfo = categories[index] || {
                      icon: "📚",
                      category: "Technology",
                      color: "from-yellow-500 to-orange-600",
                    };

                    return (
                      <Link
                        key={post.slug}
                        to={`/blog/${post.slug}`}
                        className="group block bg-gradient-to-br from-zinc-800/60 to-zinc-900/60 backdrop-blur-sm border border-zinc-700/50 rounded-2xl p-8 transition-all duration-300 hover:scale-105 hover:border-yellow-400/30 hover:shadow-xl hover:shadow-yellow-400/10"
                      >
                        <div className="mb-6">
                          <div className="flex items-center justify-between mb-4">
                            <span className="text-2xl">{categoryInfo.icon}</span>
                            <div className="flex items-center space-x-2">
                              <span className="text-xs text-zinc-300 bg-zinc-700/50 px-3 py-1 rounded-full">
                                {categoryInfo.category}
                              </span>
                              <span className="text-xs text-zinc-300 bg-zinc-700/50 px-3 py-1 rounded-full">
                                Featured
                              </span>
                            </div>
                          </div>
                          <h3 className="text-xl font-bold text-white group-hover:text-yellow-400 transition-colors duration-300 mb-3 line-clamp-2">
                            {post.title}
                          </h3>
                          {post.excerpt && <p className="text-zinc-400 text-sm line-clamp-3 mb-4">{post.excerpt}</p>}
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <time className="text-zinc-500">
                            {new Date(post.date).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </time>
                          <div className="flex items-center space-x-1 text-white-400 group-hover:text-yellow-300 transition-colors">
                            <span className="text-xs">Read more</span>
                            <svg
                              className="w-3 h-3 transform group-hover:tranzinc-x-1 transition-transform"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
            {/* All Articles Section */}
            <div className="mb-12">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-white mb-3 flex items-center justify-center">
                  <span className="text-xl mr-3">📚</span>
                  {currentPage === 1 ? "Latest Articles" : `Articles - Page ${currentPage}`}
                </h2>
                <p className="text-zinc-400">
                  {currentPage === 1
                    ? "Explore our complete collection of insights"
                    : "Continue exploring our insights"}
                </p>
              </div>

              {/* Posts Grid */}
              <div className="grid gap-6 md:gap-8">
                {(currentPage === 1 ? currentPosts.slice(3) : currentPosts).map((post, index) => (
                  <article
                    key={post.slug}
                    className="group relative bg-zinc-800/30 hover:bg-zinc-800/50 border border-zinc-700/30 hover:border-zinc-600/50 rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-zinc-900/20"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-yellow-600/5 via-transparent to-blue-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-xl"></div>

                    <div className="relative p-6 md:p-8">
                      <header className="mb-4">
                        <div className="flex items-center justify-between mb-4 gap-4">
                          <time className="flex items-center text-sm font-medium text-zinc-400 bg-zinc-700/50 px-3 py-1 rounded-full">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                            {new Date(post.date).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })}
                          </time>
                          {post.author && (
                            <span className="text-sm text-zinc-400 bg-zinc-700/50 px-3 py-1 rounded-full">
                              by {post.author}
                            </span>
                          )}
                        </div>

                        <h3 className="text-xl md:text-2xl font-bold text-white mb-3 transition-colors duration-300">
                          <Link to={`/blog/${post.slug}`} className="block">
                            {post.title}
                          </Link>
                        </h3>
                      </header>

                      {post.excerpt && (
                        <p className="text-zinc-300 mb-4 leading-relaxed line-clamp-3">{post.excerpt}</p>
                      )}

                      <div className="flex items-start justify-between gap-4 mt-8">
                        <Link
                          to={`/blog/${post.slug}`}
                          className="inline-flex items-center space-x-2 text-yellow-400 hover:text-yellow-300 font-medium transition-all duration-300 group/link whitespace-nowrap"
                        >
                          <span>Read Article</span>
                          <svg
                            className="w-4 h-4 transform group-hover/link:tranzinc-x-1 transition-transform duration-300"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M17 8l4 4m0 0l-4 4m4-4H3"
                            />
                          </svg>
                        </Link>

                        {post.tags && post.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2 justify-end">
                            {post.tags.slice(0, 2).map((tag) => (
                              <span
                                key={tag}
                                className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-full border border-zinc-500/30 text-zinc-300"
                              >
                                #{tag}
                              </span>
                            ))}
                            {post.tags.length > 2 && (
                              <span className="text-xs text-zinc-400 px-2 py-1">+{post.tags.length - 2} more</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>{" "}
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-col items-center space-y-6">
                <div className="flex items-center justify-center space-x-2">
                  {/* Previous Button */}
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                      currentPage === 1
                        ? "text-zinc-500 cursor-not-allowed bg-zinc-800/30"
                        : "text-white-400 hover:text-blue-300 hover:bg-zinc-700/50 bg-zinc-800/40 border border-zinc-700/50"
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    <span>Previous</span>
                  </button>

                  {/* Page Numbers */}
                  <div className="flex items-center space-x-2">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 min-w-[40px] ${
                          currentPage === page
                            ? "bg-gradient-to-r from-yellow-600 to-yellow-500 text-black shadow-lg"
                            : "text-zinc-400 hover:text-blue-300 hover:bg-zinc-700/50 bg-zinc-800/40 border border-zinc-700/50"
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>

                  {/* Next Button */}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                      currentPage === totalPages
                        ? "text-zinc-500 cursor-not-allowed bg-zinc-800/30"
                        : "text-white-400 hover:text-blue-300 hover:bg-zinc-700/50 bg-zinc-800/40 border border-zinc-700/50"
                    }`}
                  >
                    <span>Next</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>

                {/* Page Info */}
                {/* <div className="bg-zinc-800/30 border border-zinc-700/30 rounded-lg px-6 py-3">
                  <p className="text-zinc-400 text-sm text-center">
                    Showing <span className="text-white font-medium">{startIndex + 1}</span> to{" "}
                    <span className="text-white font-medium">{Math.min(endIndex, posts.length)}</span> of{" "}
                    <span className="text-white font-medium">{posts.length}</span> articles
                  </p>
                </div> */}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
export default BlogIndexPage;
